import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { Box, Container, Divider, Grid, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import dayjs from "dayjs";
import { SignJWT } from 'jose';
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import useNotification from "../../hooks/useNotification";
import event from "../../services/event";
import { addToCart, decreaseQuantity, filterCartByEventId } from "../../store/reducers/cart-slice";
import { HeroSection } from "./EventDetailPublic/components/HeroSection";
import { TicketCard } from "./EventDetailPublic/components/TicketCard";
import { MobileBottomCartComponent } from "./EventDetailPublic/components/MobileBottomCart";
import { DesktopCart } from "./EventDetailPublic/components/DesktopCart";
import { EventInfoSection, InfoChip } from "./EventDetailPublic/styles";
import { EventDetails, Category, RootState } from "./EventDetailPublic/types";

export default function EventDetailPublic() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const path = location.pathname.split("/");
  const event_id = path.pop() || "";
  const navigate = useNavigate();
  const notification = useNotification();

  // Target event ID for Meta Pixel
  const targetEventId = "01e773c1-4024-4205-b1c6-ce032bac0d01";
  const isTargetEvent = event_id === targetEventId;
  const targetEventIdBla = "0df1b783-6952-4bcc-ad4b-9dd03a614270";
  const isTargetEventBla = event_id === targetEventIdBla;
  const targetEventIdBlaSam = "0445a37d-4578-43bb-b503-c196c30f51e8";
  const isTargetEventBlaSam = event_id === targetEventIdBlaSam;
  const targetEventIdDisc = "42cb8edf-8dd1-4634-bf87-d66433cea058";
  const isTargetEventDisc = event_id === targetEventIdDisc;
  const targetEventIdTnbh1 = "6c1580b1-fc52-40cf-abfd-ea7635e5b7a6";
  const isTargetEventTnbh1 = event_id === targetEventIdTnbh1;
  const targetEventIdTnbh2 = "83be7aa5-d77b-4ae2-a479-276127cab7c8";
  const isTargetEventTnbh2 = event_id === targetEventIdTnbh2;

  const [eventDetails, setEventDetails] = useState<EventDetails>({
    id: String(event_id),
    name: "",
    description: "",
    photo: "",
    categories: [],
    fee: 0,
    include_tax: false,
    is_persen: false,
    ppn: 0,
    location: "",
    start_date: "",
    end_date: "",
    max_purchase: 0,
    slug: "",
    checkout_time: 10
  });

  const [showPriceBreakdown, setShowPriceBreakdown] = useState(false);

  const dispatch = useDispatch();
  const cartItems = useSelector((state: RootState) => state.cart.cartItems);
  const { filteredItems, totalTickets, totalPrice } = filterCartByEventId(
    cartItems,
    event_id
  );

  const SECRET = new TextEncoder().encode(process.env.REACT_APP_SECRET);

  const handleProceed = async () => {
    const checkoutTimeMinutes = eventDetails.checkout_time || 10; // Default to 10 minutes if not set
    const token = await new SignJWT({
      nonce: crypto.randomUUID(),
      event_id: eventDetails.slug,
      filteredItems: filteredItems,
      totalTickets: totalTickets,
      totalPrice: totalPrice
    })
      .setProtectedHeader({ alg: "HS256" })
      .setExpirationTime(`${checkoutTimeMinutes}m`)
      .sign(SECRET);

    sessionStorage.setItem("formToken", token);
    navigate("/order", { state: { token } });
  };

  useEffect(() => {
    event
      .viewPublicEventDetail({ id: event_id, is_category: true })
      .then((response) => {
        setEventDetails(response.data);
      });
  }, [event_id]);

  const handleAdd = (category: Category) => {
    const cartItem = filteredItems.find(
      (item: any) => item.category_id === category.id
    );
    const currentQuantity = cartItem?.quantity || 0;

    if (
      category.is_available &&
      currentQuantity < eventDetails.max_purchase
    ) {
      try {
        dispatch(
          addToCart({
            category_id: category.id,
            quantity: 1,
            price: category.price,
            event_id,
            category_name: category.name,
            time:
              dayjs(eventDetails.start_date).format("MM/DD HH:mm") +
              " - " +
              dayjs(eventDetails.end_date).format("MM/DD HH:mm"),
          })
        );
      } catch (error: any) {
        notification.onOpen({
          message: error.message,
          type: "error",
          position: "top",
        });
      }
    } else if (!category.is_available) {
      notification.onOpen({
        message: "This ticket is not available",
        type: "error",
        position: "top",
      });
    } else if (currentQuantity >= eventDetails.max_purchase) {
      notification.onOpen({
        message: `Maximum ${eventDetails.max_purchase} tickets allowed per purchase`,
        type: "error",
        position: "top",
      });
    }
  };

  const handleDecrease = (category: Category) => {
    dispatch(
      decreaseQuantity({
        category_id: category.id,
      })
    );
  };

  const totalFinalPrice = Number(totalPrice) +
    Number(eventDetails.include_tax ? 0 : (eventDetails.ppn * totalPrice) / 100) +
    Number(eventDetails.is_persen ? (eventDetails.fee * totalPrice) / 100 : eventDetails.fee);

  return (
    <MainLayout>
      <Helmet>
        <title>{eventDetails.name}</title>
        {isTargetEvent && (
          <script>
            {`
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '1449253282904632');
              fbq('track', 'PageView');
            `}
          </script>
        )}
        {isTargetEvent && (
          <noscript>
            {`
              <img height="1" width="1" style="display:none"
              src="https://www.facebook.com/tr?id=1449253282904632&ev=PageView&noscript=1"
              />
            `}
          </noscript>
        )}
        {isTargetEventBla && (
          <script>
            {`
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod ?
              n.callMethod.apply(n, arguments) : n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '1915385735672706');
              fbq('track', 'PageView');
            `}
          </script>
        )}
        {isTargetEventBla && (
          <noscript>
            {`
              <img height="1" width="1" style="display:none"
              src="https://www.facebook.com/tr?id=1915385735672706&ev=PageView&noscript=1"
              />
            `}
          </noscript>
        )}
        {isTargetEventBlaSam && (
          <script>
            {`
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '307748501929696');
              fbq('track', 'PageView');
            `}
          </script>
        )}
        {isTargetEventBlaSam && (
          <noscript>
            {`
              <img height="1" width="1" style="display:none"
              src="https://www.facebook.com/tr?id=307748501929696&ev=PageView&noscript=1"
              />
            `}
          </noscript>
        )}
        {isTargetEventDisc && (
          <script>
            {`
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '405208281989892');
              fbq('track', 'PageView');

            `}
          </script>
        )}
        {isTargetEventDisc && (
          <noscript>
            {`
              <img height="1" width="1" style="display:none"
              src="https://www.facebook.com/tr?id=405208281989892&ev=PageView&noscript=1"
              />
            `}
          </noscript>
        )}
        {isTargetEventDisc && (
          <script>
            {`
              !function (w, d, t) {
                w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(
              var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=r,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};n=document.createElement("script")
              ;n.type="text/javascript",n.async=!0,n.src=r+"?sdkid="+e+"&lib="+t;e=document.getElementsByTagName("script")[0];e.parentNode.insertBefore(n,e)};


                ttq.load('D41UU1RC77UA61AHK9BG');
                ttq.page();
              }(window, document, 'ttq');
            `}
          </script>
        )}
        {isTargetEventTnbh1 && (
          <script>
            {`
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '850420403769069');
              fbq('track', 'PageView');
            `}
          </script>
        )}
        {isTargetEventTnbh1 && (
          <noscript>
            {`
              <img height="1" width="1" style="display:none"
              src="https://www.facebook.com/tr?id=850420403769069&ev=PageView&noscript=1"
              />
            `}
          </noscript>
        )}
        {isTargetEventTnbh1 && (
          <script>
            {`
              !function (w, d, t) {
                w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(
              var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=r,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};n=document.createElement("script")
              ;n.type="text/javascript",n.async=!0,n.src=r+"?sdkid="+e+"&lib="+t;e=document.getElementsByTagName("script")[0];e.parentNode.insertBefore(n,e)};


                ttq.load('D4563OJC77U8B5BDOKOG');
                ttq.page();
              }(window, document, 'ttq');
            `}
          </script>
        )}
        {isTargetEventTnbh2 && (
          <script>
            {`
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '969926137586568');
              fbq('track', 'PageView');
            `}
          </script>
        )}
        {isTargetEventTnbh2 && (
          <noscript>
            {`
              <img height="1" width="1" style="display:none"
              src="https://www.facebook.com/tr?id=969926137586568&ev=PageView&noscript=1"
              />
            `}
          </noscript>
        )}
      </Helmet>
      <Container maxWidth="xl" sx={{ px: { xs: 2, md: 3 } }}>
        {!isMobile && (
          <Breadcrumb
            label={eventDetails.name}
            breadcrumbs={[
              { label: "Events", href: "/" },
              { label: eventDetails.name, href: "#" },
              { label: "Select Tickets" },
            ]}
          />
        )}
      </Container>
      <HeroSection eventDetails={eventDetails} isMobile={isMobile} />

      {/* Event Info Section (Mobile) */}
      <EventInfoSection maxWidth="xl" sx={{ px: { xs: 2, md: 3 } }}>
        {isMobile && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h4"
              component="h1"
              fontWeight="bold"
              sx={{ mb: 2, fontSize: '1.5rem' }}
            >
              {eventDetails.name}
            </Typography>

            <Stack spacing={1} sx={{ mb: 2 }}>
              <InfoChip
                className="location"
                icon={<LocationOnIcon />}
                label={eventDetails.location}
              />
              <InfoChip
                className="date"
                icon={<CalendarTodayIcon />}
                label={dayjs(eventDetails.start_date).format("dddd, MMMM DD, YYYY")}
              />
              <InfoChip
                className="time"
                icon={<AccessTimeIcon />}
                label={`${dayjs(eventDetails.start_date).format("h:mm A")} - ${dayjs(eventDetails.end_date).format("h:mm A")}`}
              />
            </Stack>

            <Typography
              variant="body1"
              color="textSecondary"
              sx={{ lineHeight: 1.6, mb: 2 }}
            >
              {eventDetails.description}
            </Typography>

            <Divider sx={{ my: 3 }} />
          </Box>
        )}

        <Grid container spacing={4}>
          {/* Tickets Section */}
          <Grid item xs={12} lg={8}>
            <Box sx={{ mb: { xs: 10, md: 4 } }}>
              <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
                Available Tickets
              </Typography>

              <Stack spacing={2}>
                {eventDetails.categories.map((category, index) => {
                  const cartItem = filteredItems.find(
                    (item: any) => item.category_id === category.id
                  );

                  return (
                    <TicketCard
                      key={category.id}
                      category={category}
                      cartItem={cartItem}
                      eventDetails={eventDetails}
                      index={index}
                      onAdd={handleAdd}
                      onDecrease={handleDecrease}
                    />
                  );
                })}
              </Stack>
            </Box>
          </Grid>

          {/* Desktop Cart Section */}
          <Grid item xs={12} lg={4}>
            <DesktopCart
              filteredItems={filteredItems}
              totalTickets={totalTickets}
              totalPrice={totalPrice}
              totalFinalPrice={totalFinalPrice}
              eventDetails={eventDetails}
              onProceed={handleProceed}
            />
          </Grid>
        </Grid>
      </EventInfoSection>

      {/* Mobile Bottom Cart */}
      {filteredItems.length > 0 && (
        <MobileBottomCartComponent
          filteredItems={filteredItems}
          totalTickets={totalTickets}
          totalFinalPrice={totalFinalPrice}
          totalPrice={totalPrice}
          eventDetails={eventDetails}
          onProceed={handleProceed}
          showPriceBreakdown={showPriceBreakdown}
          setShowPriceBreakdown={setShowPriceBreakdown}
        />
      )}
    </MainLayout>
  );
}